import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Container,
  Heading,
  Flex,
  EditableCells,
  EditableHeader,
  Table,
  Column,
  useFetch,
  useToggle,
  AddCircleIcon,
  RemoveCircleIcon,
  EditIcon,
  useSort,
  LineArrowIcon,
} from "@pacerpro/shared-ui";
import { normalizeSort } from "@pacerpro/utils";
import { Organizations, Offices, Users } from "@pacerpro/api";
import {
  selectSession,
  selectOrganizationTabData,
  selectOffices,
} from "../selectors";
import {
  setOrganization,
  setOrganizationDrawer,
  editOrganization,
  setSelectedAdmin,
  removeAdmin,
} from "../reducers/organizations";
import {
  setOffices,
  editOfficeInTable,
  setEmptyOffice,
  addNewOffice,
  deleteOffice,
} from "../reducers/offices";
import Page from "../components/Layout/Page";
import Navigation from "../components/Navigation";
import OrganizationDrawer from "../components/organization/OrganizationDrawer";
import { SortDirectionType } from "react-virtualized";

const useDrawerToggle = () => {
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose, onToggle } = useToggle();
  return {
    isDrawerOpen: isOpen,
    onDrawerClose: () => {
      onClose();
      dispatch(
        setSelectedAdmin({
          data: undefined,
          included: [],
        }),
      );
    },
    onDrawerOpen: () => {
      dispatch(setOrganizationDrawer("admin"));
      onOpen();
    },
    onDrawerToggle: () => {
      onToggle();
      if (!isOpen) {
        dispatch(setOrganizationDrawer("admin"));
      } else {
        dispatch(
          setSelectedAdmin({
            data: undefined,
            included: [],
          }),
        );
      }
    },
  };
};

const OrganizationPage = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectSession);
  const offices = useSelector(selectOffices);

  const {
    isDrawerOpen,
    onDrawerOpen,
    onDrawerClose,
    onDrawerToggle,
  } = useDrawerToggle();

  const { sort, direction, onSort } = useSort({
    sortBy: "name",
    sortDirection: "ASC",
  });

  const [officesResponse, officesLoading] = useFetch(Offices.getAll, {
    sort: normalizeSort(sort, direction),
  });

  React.useEffect(() => {
    if (session.organizationId) {
      Organizations.getById(session.organizationId, {
        include: ["users"],
      }).then((response: any) => dispatch(setOrganization(response.data)));
    }
  }, [session]);

  React.useEffect(() => {
    if (!officesLoading && officesResponse) {
      dispatch(setOffices(officesResponse));
    }
  }, [officesLoading, officesResponse]);

  const handleSort = ({
    sortBy,
    sortDirection,
  }: {
    sortBy: string;
    sortDirection: SortDirectionType;
  }) => {
    onSort({ sortBy, sortDirection });
    return Offices.getAll({
      sort: normalizeSort(sortBy, sortDirection),
    }).then((response: any) => dispatch(setOffices(response.data)));
  };

  const organization = useSelector(selectOrganizationTabData);

  if (!organization) {
    return null;
  }

  const organizationDrawer = React.useMemo(
    () => (
      <OrganizationDrawer
        isOpen={isDrawerOpen}
        onOpen={onDrawerOpen}
        onClose={onDrawerClose}
        onToggle={onDrawerToggle}
      />
    ),
    [isDrawerOpen],
  );

  const onEdit = async (adminId: string) => {
    try {
      const adminResponse = await Users.getById(adminId);
      dispatch(setSelectedAdmin(adminResponse.data));
    } catch (error) {
      console.error(`Error getting user: ${error}`);
    }
    onDrawerOpen();
  };

  const editingNewOffice =
    offices && offices.length > 0 && offices[0].status === "new";

  return (
    <Page title="Organization">
      <Container>
        <Navigation active="organization" />
        <Flex direction="column" p={4}>
          <Box my={6}>
            <EditableHeader
              // TODO: (at) add onEdit capability once API handles it
              onEdit={newValue =>
                dispatch(
                  editOrganization(organization.organizationId, {
                    name: newValue,
                  }),
                )
              }
              cell={{
                value: organization.name,
                placeholder: "Organization Name *",
                type: "text",
              }}
            />
          </Box>
          <Box height="90px" my={6}>
            <Table variant="primary" data={[organization.primaryContact]}>
              <Column
                dataKey="primaryContactName"
                width={100}
                flexGrow={1}
                headerRenderer={() => (
                  <Heading as="h3" color="black" fontWeight="normal">
                    Primary Contact
                  </Heading>
                )}
                cellRenderer={({ rowData }) => (
                  <EditableCells
                    iconHover
                    cell={{
                      value: rowData.name,
                      placeholder: "Name",
                      type: "text",
                      name: "primaryContactName",
                    }}
                    onEdit={newValues =>
                      dispatch(
                        editOrganization(organization.organizationId, newValues),
                      )
                    }
                  />
                )}
              />
              <Column
                label="Email"
                dataKey="primaryContactEmail"
                cellRenderer={({ rowData }) => (
                  <EditableCells
                    iconHover
                    cell={{
                      value: rowData.email,
                      placeholder: "Email",
                      type: "email",
                      name: "primaryContactEmail",
                    }}
                    onEdit={newValues =>
                      dispatch(
                        editOrganization(organization.organizationId, newValues),
                      )
                    }
                  />
                )}
                width={100}
                flexGrow={1}
              />
              <Column
                label="Phone"
                dataKey="primaryPhone"
                cellRenderer={({ rowData }) => (
                  <EditableCells
                    iconHover
                    cell={{
                      value: rowData.phone,
                      placeholder: "Phone",
                      type: "tel",
                      name: "primaryContactPhone",
                    }}
                    onEdit={newValues =>
                      dispatch(
                        editOrganization(organization.organizationId, newValues),
                      )
                    }
                  />
                )}
                width={100}
                flexGrow={1}
              />
              <Column
                label="Location"
                dataKey="primaryContactLocation"
                cellRenderer={({ rowData }) => (
                  <EditableCells
                    iconHover
                    cell={{
                      value: rowData.location,
                      placeholder: "Location",
                      type: "text",
                      name: "primaryContactLocation",
                    }}
                    onEdit={newValues =>
                      dispatch(
                        editOrganization(organization.organizationId, newValues),
                      )
                    }
                  />
                )}
                width={100}
                flexGrow={1}
              />
            </Table>
          </Box>
          <Box height="235px" my={6}>
            <Table
              variant="primary"
              data={offices}
              sort={handleSort}
              sortBy={sort}
              sortDirection={direction}
            >
              <Column
                dataKey="name"
                cellRenderer={({ rowData }) => (
                  <EditableCells
                    cell={{
                      value: rowData.attributes.name,
                      placeholder: "Name *",
                      type: "text",
                      name: "name",
                    }}
                    open={rowData.status === "new" ? true : false}
                    onEdit={newValues =>
                      rowData.status === "new"
                        ? dispatch(addNewOffice(newValues))
                        : dispatch(editOfficeInTable(rowData.id, newValues))
                    }
                  />
                )}
                className="editable"
                width={100}
                flexGrow={1}
                headerRenderer={({ sortDirection }) => (
                  <Flex direction="row" align="center">
                    <Heading as="h3" color="black" fontWeight="normal">
                      Offices
                    </Heading>
                    <LineArrowIcon
                      direction={sortDirection === "ASC" ? "up" : "down"}
                      size="18px"
                      ml={1}
                      sx={{ color: "red" }}
                    />
                  </Flex>
                )}
              />
              <Column
                label="PACER ID"
                dataKey="pacerUsername"
                cellRenderer={({ rowData }) => (
                  <EditableCells
                    cell={{
                      value: rowData.attributes.pacerUsername,
                      placeholder: "PACER ID",
                      type: "text",
                      name: "pacerUsername",
                    }}
                    open={rowData.status === "new" ? true : false}
                    shouldFocus={rowData.status === "new" ? false : true}
                    onEdit={newValues =>
                      dispatch(editOfficeInTable(rowData.id, newValues))
                    }
                  />
                )}
                className="editable"
                width={100}
                flexGrow={1}
                disableSort={true}
              />
              <Column
                label="PACER PW"
                dataKey="pacerPassword"
                cellRenderer={({ rowData }) => (
                  <EditableCells
                    cell={{
                      value: rowData.attributes.pacerPassword,
                      placeholder: "PACER Password",
                      type: "password",
                      name: "pacerPassword",
                    }}
                    open={rowData.status === "new" ? true : false}
                    shouldFocus={rowData.status === "new" ? false : true}
                    onEdit={newValues =>
                      dispatch(editOfficeInTable(rowData.id, newValues))
                    }
                  />
                )}
                className="editable"
                width={100}
                flexGrow={1}
                disableSort={true}
              />
              <Column
                dataKey="action"
                width={100}
                flexGrow={1}
                headerRenderer={() => (
                  <Flex justify="flex-end">
                    <AddCircleIcon
                      sx={{
                        cursor: editingNewOffice ? "cusor" : "pointer",
                      }}
                      color="primary"
                      onClick={() =>
                        !editingNewOffice &&
                        dispatch(
                          setEmptyOffice({
                            id: "",
                            status: "new",
                            type: "offices",
                            attributes: {},
                          }),
                        )
                      }
                    />
                  </Flex>
                )}
                cellRenderer={({ rowData }) => (
                  <Flex justify="flex-end">
                    <RemoveCircleIcon
                      sx={{
                        mr: "2px",
                        cursor: "pointer",
                      }}
                      variant="outline"
                      color="primary"
                      size="20px"
                      onClick={() => dispatch(deleteOffice(rowData.id))}
                    />
                  </Flex>
                )}
                disableSort={true}
              />
            </Table>
          </Box>
          <Box height="235px" my={6}>
            <Table variant="primary" data={organization.admins}>
              <Column
                dataKey="fullName"
                cellDataGetter={({ rowData }) =>
                  `${rowData.firstName || "----"} ${rowData.lastName || "----"}`
                }
                className="editable"
                width={100}
                flexGrow={1}
                headerRenderer={() => (
                  <Heading as="h3" color="black" fontWeight="normal">
                    Administrators
                  </Heading>
                )}
              />
              <Column
                label="Email"
                dataKey="email"
                cellDataGetter={({ rowData }) => rowData.email || "----"}
                width={100}
                flexGrow={1}
              />
              <Column
                label="Office"
                dataKey="office"
                cellDataGetter={({ rowData }) => rowData.office || "----"}
                width={100}
                flexGrow={1}
              />
              <Column
                dataKey="action"
                // TODO: (at) commenting out adding an admin until new designs come in
                // headerRenderer={() => (
                //   <Flex justify="flex-end">
                //     <AddCircleIcon
                //       onClick={onDrawerOpen}
                //       sx={{ cursor: "pointer" }}
                //       color="primary"
                //     />
                //   </Flex>
                // )}
                cellRenderer={({ rowData }) => (
                  <Flex justify="flex-end">
                    <EditIcon
                      color="primary"
                      size="20px"
                      sx={{
                        mr: 3,
                        cursor: "pointer",
                      }}
                      onClick={() => onEdit(rowData.userId)}
                    />
                    <RemoveCircleIcon
                      sx={{
                        mr: "2px",
                        cursor: "pointer",
                      }}
                      variant="outline"
                      color="primary"
                      size="20px"
                      onClick={() => dispatch(removeAdmin(rowData.userId))}
                    />
                  </Flex>
                )}
                width={100}
                flexGrow={1}
              />
            </Table>
          </Box>
        </Flex>
        {organizationDrawer}
      </Container>
    </Page>
  );
};

export default OrganizationPage;
